import { useState, useCallback } from 'react';
import { useLocale } from 'next-intl';
import { useGateway } from './useGateway';
import { BlogPost, PaginatedBlogPosts, PaginationQuery, BlogFilters } from '../types/blog';

/**
 * Hook for blog operations using the service-based architecture
 */
export const useBlogService = () => {
  const { gateway, handleRequest, isLoading, error } = useGateway();
  const locale = useLocale();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [pagination, setPagination] = useState<Omit<PaginatedBlogPosts, 'items'> | null>(null);

  /**
   * Get published blog posts with pagination
   */
  const getPublishedPosts = useCallback(async (query: PaginationQuery = {}): Promise<PaginatedBlogPosts | null> => {
    const result = await handleRequest(() => gateway.blog.getPublishedPosts(query, locale));
    if (result?.items) {
      setPosts(result.items);
      setPagination({
        total: result.total,
        page: result.page,
        limit: result.limit,
        totalPages: result.totalPages
      });
    }
    return result;
  }, [gateway.blog, handleRequest, locale]);

  /**
   * Get blog post by slug
   */
  const getPostBySlug = useCallback(async (slug: string): Promise<BlogPost | null> => {
    const result = await handleRequest(() => gateway.blog.getPostBySlug(slug, locale));
    if (result) {
      setPost(result);
    }
    return result;
  }, [gateway.blog, handleRequest, locale]);

  /**
   * Search published blog posts
   */
  const searchPosts = useCallback(async (query: string, options: Omit<BlogFilters, 'search'> = {}): Promise<PaginatedBlogPosts | null> => {
    const result = await handleRequest(() => gateway.blog.searchPosts(query, options, locale));
    if (result?.items) {
      setPosts(result.items);
      setPagination({
        total: result.total,
        page: result.page,
        limit: result.limit,
        totalPages: result.totalPages
      });
    }
    return result;
  }, [gateway.blog, handleRequest, locale]);

  /**
   * Get posts by tag
   */
  const getPostsByTag = useCallback(async (tag: string, options: Omit<BlogFilters, 'tags'> = {}): Promise<PaginatedBlogPosts | null> => {
    const result = await handleRequest(() => gateway.blog.getPostsByTag(tag, options, locale));
    if (result?.items) {
      setPosts(result.items);
      setPagination({
        total: result.total,
        page: result.page,
        limit: result.limit,
        totalPages: result.totalPages
      });
    }
    return result;
  }, [gateway.blog, handleRequest, locale]);

  /**
   * Get posts by author
   */
  const getPostsByAuthor = useCallback(async (author: string, options: Omit<BlogFilters, 'author'> = {}): Promise<PaginatedBlogPosts | null> => {
    const result = await handleRequest(() => gateway.blog.getPostsByAuthor(author, options, locale));
    if (result?.items) {
      setPosts(result.items);
      setPagination({
        total: result.total,
        page: result.page,
        limit: result.limit,
        totalPages: result.totalPages
      });
    }
    return result;
  }, [gateway.blog, handleRequest, locale]);

  /**
   * Clear current posts
   */
  const clearPosts = useCallback(() => {
    setPosts([]);
    setPagination(null);
  }, []);

  /**
   * Clear current post
   */
  const clearPost = useCallback(() => {
    setPost(null);
  }, []);

  return {
    // State
    posts,
    post,
    pagination,
    isLoading,
    error,

    // Actions
    getPublishedPosts,
    getPostBySlug,
    searchPosts,
    getPostsByTag,
    getPostsByAuthor,
    clearPosts,
    clearPost
  };
};
